import { useState } from 'react';
import { Eye, EyeOff, Plus, Trash2 } from 'lucide-react';
import { MAX_CUSTOM_CATEGORIES, setImportSourceDeadlines, sourceTreatsAsDeadlines, type Category, type IconId } from '@autoday/core';
import { getStore, useStore } from '../store/appStore.ts';
import { Badge, Button, Field, Panel, inputCls } from '../ui/kit.tsx';
import { catColor } from '../ui/format.ts';
import { ICONS, ItemIcon } from '../ui/icons.tsx';
import { errorText, toast } from '../ui/toast.tsx';
import { randomId } from '../platform.ts';
import { useLang } from '../i18n/i18n.ts';
import { calendarName, categoryName, iconLabel } from '../i18n/format.ts';

export function CalendarsView() {
  const { state } = useStore();
  const store = getStore();
  const { t } = useLang();
  const [name, setName] = useState('');
  const [icon, setIcon] = useState<IconId>('star');
  const custom = Object.values(state.categories).filter((c) => !c.builtIn);

  const save = (fn: Parameters<typeof store.replace>[0], ok?: string) =>
    store
      .replace(fn)
      .then(() => ok && toast({ kind: 'ok', text: ok }))
      .catch((e) => toast({ kind: 'error', text: errorText(e) }));

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5 px-4 py-5 lg:px-8">
      <h1 className="text-2xl font-semibold tracking-tight">{t('Mina kalendrar', 'My calendars')}</h1>
      <Panel className="p-2">
        <ul>
          {Object.values(state.calendars).map((c) => {
            const source = c.sourceId ? state.sources[c.sourceId] : undefined;
            const deadlines = c.kind === 'import' && sourceTreatsAsDeadlines(source, c.sourceId);
            return (
              <li key={c.id} className="flex min-h-14 flex-wrap items-center gap-3 px-3 py-2">
                <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: catColor(c.color) }} aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="font-medium">{calendarName(c)}</span>
                  <span className="block text-xs text-muted">
                    {c.kind === 'import'
                      ? t('Importerad fil (ögonblicksbild) – skrivskyddad', 'Imported file (snapshot) – read-only')
                      : c.kind === 'work'
                        ? t('Egen – arbete', 'Own – work')
                        : t('Egen – privat', 'Own – personal')}
                  </span>
                  {!c.visible && <span className="block text-xs text-muted">{t('Dold i kalendern. Räknas ändå som upptagen tid när AutoDay söker tid att förbereda.', 'Hidden in the calendar. Still counts as busy time when AutoDay looks for preparation time.')}</span>}
                </span>
                {c.kind === 'import' && <Badge tone="source">Import</Badge>}
                <Button
                  aria-pressed={c.visible}
                  icon={c.visible ? <Eye size={16} aria-hidden /> : <EyeOff size={16} aria-hidden />}
                  onClick={() => void save((s) => ({ ...s, calendars: { ...s.calendars, [c.id]: { ...c, visible: !c.visible } } }))}
                >
                  {c.visible ? t('Visas', 'Shown') : t('Dold', 'Hidden')}
                </Button>
                {c.kind === 'import' && source && (
                  <label className="flex min-h-11 w-full items-start gap-2 rounded-lg bg-bg/60 px-3 py-2 text-sm sm:ml-6">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-5 w-5"
                      checked={deadlines}
                      onChange={(e) => {
                        // Värdet läses direkt: kontrollerade rutor återställs efter händelsen, innan sparandet körs.
                        const on = e.target.checked;
                        void save((s) => setImportSourceDeadlines(s, source.id, on), on ? t('Posterna visas under Viktigt.', 'The items are shown under Deadlines.') : t('Posterna visas inte längre under Viktigt.', 'The items are no longer shown under Deadlines.'));
                      }}
                    />
                    <span>
                      {t('Räkna posterna som deadlines under Viktigt', 'Count the items as deadlines under Deadlines')}
                      <span className="block text-xs text-muted">
                        {t(
                          'Standardvalet beror på importkällan. Ett importerat möte är inte automatiskt en deadline. Enskilda poster kan ändras i posten. Datum och påminnelser påverkas inte.',
                          'The default depends on the import source. An imported meeting is not automatically a deadline. Single items can be changed in the item. Dates and reminders are not affected.',
                        )}
                      </span>
                    </span>
                  </label>
                )}
              </li>
            );
          })}
        </ul>
      </Panel>

      <Panel className="p-5">
        <h2 className="text-base font-semibold">{t('Kategorier', 'Categories')}</h2>
        <p className="text-sm text-muted">{t('Kategori och symbol är din egen sortering. De är inte samma sak som en källtagg eller prioritet.', 'Category and symbol are your own sorting. They are not the same as a source tag or priority.')}</p>
        <ul className="mt-3 flex flex-wrap gap-2">
          {Object.values(state.categories).map((c: Category) => (
            <li key={c.id} className="flex min-h-11 items-center gap-2 rounded-lg border border-border px-3 text-sm">
              <ItemIcon icon={c.icon} />
              {categoryName(c)}
              {c.sensitive && <Badge title={t('Döljs på låsskärmen om inställningen är på', 'Hidden on the lock screen if the setting is on')}>{t('Känslig', 'Sensitive')}</Badge>}
              {!c.builtIn && (
                <button
                  aria-label={t(`Ta bort kategorin ${c.name}`, `Delete the category ${c.name}`)}
                  className="grid min-h-11 min-w-11 place-items-center text-muted hover:text-risk"
                  onClick={() => {
                    const used = Object.values(state.events).some((e) => !e.deleted && e.categoryId === c.id);
                    if (used) return toast({ kind: 'warn', text: t('Kategorin används av aktiviteter. Byt kategori på dem först.', 'The category is used by activities. Change their category first.') });
                    void save((s) => {
                      const cats = { ...s.categories };
                      delete cats[c.id];
                      return { ...s, categories: cats };
                    }, t('Kategorin borttagen.', 'Category deleted.'));
                  }}
                >
                  <Trash2 size={16} aria-hidden />
                </button>
              )}
            </li>
          ))}
        </ul>
        {custom.length < MAX_CUSTOM_CATEGORIES && (
          <form
            className="mt-4 grid gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!name.trim()) return;
              const id = randomId('cat_');
              void save((s) => ({ ...s, categories: { ...s.categories, [id]: { id, name: name.trim().slice(0, 40), icon, builtIn: false } } }), t('Kategori skapad.', 'Category created.'));
              setName('');
            }}
          >
            <Field label={t('Ny kategori', 'New category')} htmlFor="cat-name">
              <input id="cat-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} maxLength={40} />
            </Field>
            <div role="radiogroup" aria-label={t('Symbol för kategori', 'Symbol for category')} className="flex flex-wrap gap-1.5">
              {(Object.keys(ICONS) as IconId[]).map((id) => {
                const C = ICONS[id];
                return (
                  <button type="button" key={id} role="radio" aria-checked={icon === id} aria-label={iconLabel(id)} onClick={() => setIcon(id)} className={`grid h-11 w-11 place-items-center rounded-lg border ${icon === id ? 'border-accent text-accent' : 'border-border text-muted'}`}>
                    <C size={18} aria-hidden />
                  </button>
                );
              })}
            </div>
            <Button type="submit" icon={<Plus size={16} aria-hidden />} disabled={!name.trim()}>
              {t('Lägg till kategori', 'Add category')}
            </Button>
          </form>
        )}
      </Panel>
    </div>
  );
}
